import { getHeaders } from "../ApiHeaders/apiHeadersEntitlements";

export const fetchEntitlementsFromApiOnly = async () => {
  const objectId = localStorage.getItem("userObjectId");
  if (!objectId) {
    throw new Error("User object ID is missing from localStorage.");
  }

  const sessionKey = `entitlements-${objectId}`;

  const headers = await getHeaders();
  const apiBaseUrl = process.env.REACT_APP_ENTITLEMENTS_API;
  if (!apiBaseUrl) {
    throw new Error("API URL is missing");
  }

  const apiUrl = `${apiBaseUrl}/entitlements-mapping/user?entra_object_id=${objectId}&po_type_code=UI_ELEMENT`;
  const response = await fetch(apiUrl, { method: "GET", headers });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} - ${response.statusText}`);
  }

  const json = await response.json();
  const apiData = json?.response?.data;

  if (!apiData || !Array.isArray(apiData)) {
    throw new Error("No valid entitlements data found for the user.");
  }

  // Filter only UI_ELEMENT entitlements (case-insensitive — API may return lowercase)
  const filteredData = apiData.filter(
    (item) => item.po_type_code?.toUpperCase() === "UI_ELEMENT"
  );

  const payload = { data: filteredData };

  // Only cache a non-empty result. An empty array is indistinguishable from a
  // real answer once it is in sessionStorage, and the read path below would
  // serve it for the rest of the session -- so a single bad response (a cold
  // entitlements instance during a deploy, say) hides every nav tab until the
  // user clears storage. Not caching means the next call simply retries.
  if (filteredData.length > 0) {
    // Store only in sessionStorage (no CacheDB/localStorage)
    sessionStorage.setItem(sessionKey, JSON.stringify(payload));
  } else {
    console.warn(
      "Entitlements API returned no UI_ELEMENT rows; not caching so the next load retries."
    );
    sessionStorage.removeItem(sessionKey);
  }

  return payload;
};

export const fetchAndCacheEntitlements = async () => {
  try {
    const objectId = localStorage.getItem("userObjectId");
    if (!objectId) {
      throw new Error("User object ID is missing from localStorage.");
    }

    const sessionKey = `entitlements-${objectId}`;

    // Try sessionStorage first -- but only if it holds a non-empty result.
    // A cached empty array is never a legitimate answer for a logged-in user,
    // and trusting it pins the session to "no entitlements".
    const sessionData = sessionStorage.getItem(sessionKey);
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (Array.isArray(parsed?.data) && parsed.data.length > 0) {
          return parsed;
        }
        console.warn("Empty entitlements in sessionStorage. Refetching...");
        sessionStorage.removeItem(sessionKey);
      } catch {
        console.warn("Invalid JSON in sessionStorage. Ignoring...");
      }
    }

    // Always fetch fresh data from API if not found
    return await fetchEntitlementsFromApiOnly();
  } catch (error) {
    console.error("Error in fetchAndCacheEntitlements:", error);
    throw error;
  }
};
